import axios from 'axios';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import ActivityItem from '../ActivityItem';
import { ActivityData, Point, UserInfo } from '../index';
import { AlertContext } from '../../App';
import IconButton, { ButtonType } from '../IconButton';
import { formatNumber, translatePlaceDescription } from '../../util';
import { getErrorMessage } from '../../util/message';

import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';

interface ActivityRequestPayload {
  offset?: number;
  placeId: string;
  uuid: string;
}

interface ActivityResponse {
  activities: {
    created_at: string;
    points: number;
    store: {
      id: string;
      name: string;
    };
    type: string;
  }[];
}

const LIMIT = 10;

const getActivity = async (payload: ActivityRequestPayload): Promise<ActivityData[]> => {
  const { offset = 0, placeId, uuid } = payload;

  const config = {
    params: {
      offset,
      limit: LIMIT,
    },
  };
  const response = await axios.get<ActivityResponse>(`/mobile/users/${uuid}/places/${placeId}/activities/`, config);

  const { data } = response;
  const activities = data.activities.map<ActivityData>((activity) => ({
    createdAt: activity.created_at,
    points: activity.points,
    store: {
      id: activity.store.id,
      name: activity.store.name,
    },
    type: activity.type,
  }));

  return activities;
};

interface Props {
  point: Point;
  userInfo: UserInfo;
  onClose?(): void;
}

const Activity: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { onClose, point, userInfo } = props;
  const alert = useContext(AlertContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [activities, setActivities] = useState<ActivityData[]>([]);
  const layoutContainer = useRef<HTMLDivElement>(null);
  const handleFailure = useCallback(
    (message: string) => {
      alert({
        content: message,
        type: 'error',
      });
    },
    [alert]
  );

  const fetchActivity = async (offset: number) => {
    setLoading(true);
    const payload: ActivityRequestPayload = {
      offset: (offset - 1) * LIMIT,
      placeId: point.place.id,
      uuid: userInfo.uuid,
    };

    try {
      const newActivities = await getActivity(payload);
      setActivities(activities.concat(newActivities));
      setHasMore(newActivities.length > 0 && newActivities.length % LIMIT === 0);
    } catch (err) {
      handleFailure(getErrorMessage(err));
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchActivity(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getContainerRef = (): HTMLDivElement | null => {
    return layoutContainer.current;
  };

  return (
    <div className={styles.fullScreenLayout}>
      <div className={styles.container} ref={layoutContainer}>
        <div className={styles.header}>
          <IconButton data-id="btn_back" type={ButtonType.Back} onClick={onClose} />
        </div>

        <div className={styles.info}>
          <p className={styles.placeName}>{point.place.name}</p>
          <p className={styles.savedPoint}>
            {formatNumber(point.point.points)} <span className={styles.point}>P</span>
          </p>
          <p className={styles.description}>{translatePlaceDescription(point)}</p>
        </div>

        <div className={styles.points}>
          <p>{t('포인트 내역')}</p>
          <InfiniteScroll
            pageStart={1}
            loadMore={fetchActivity}
            hasMore={!loading && hasMore}
            initialLoad={false}
            useWindow={false}
            getScrollParent={getContainerRef}
          >
            {activities.map((activity) => (
              <ActivityItem key={activity.createdAt} activity={activity} />
            ))}
          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
};

export default Activity;
